import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Flex, Select, Spin, Tag, TreeSelect, Typography } from "antd";
import {
  CloseCircleOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { LateralModal } from "@/components/modals/LateralModal";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import { etiquetaConjunto } from "./conjuntos";
import { ESTADOS } from "./estados";
import type { CategoryNode } from "./types";
import type { CatalogFilters as CatalogFiltersState } from "./useCatalogList";

/** Los atributos de tipo lista que algún producto del índice tiene cargados (xcon-api!1019). */
interface AtributoFiltrable {
  attributeId: number;
  code: string;
  label: string | null;
  frontendInput: "select" | "multiselect";
  optionCount: number;
}

interface OpcionDeFiltro {
  optionId: number;
  label: string;
  productCount: number;
}

/**
 * Las etiquetas de lo elegido, para que un filtro aplicado se lea al volver a abrir el panel.
 * Vive a nivel de módulo porque el panel se desmonta al cerrarse, y las opciones se piden de a
 * pedazos (con búsqueda): la etiqueta de una opción elegida puede no estar en la última página.
 */
const etiquetasDeOpcion = new Map<string, string>();
const claveOpcion = (code: string, id: number) => `${code}:${id}`;

/**
 * Lo mismo para los nombres de categoría y de atributo: los usan también las etiquetas de
 * filtros aplicados, que se ven con el panel cerrado.
 */
const nombresDeCategoria = new Map<number, string>();
const nombresDeAtributo = new Map<string, string>();

const pedirAtributos = () =>
  axiosInstance
    .get<AtributoFiltrable[]>(`${API_URL_PIM_PRODUCTS}/filters/attributes`)
    .then(({ data }) => {
      for (const a of data ?? []) nombresDeAtributo.set(a.code, a.label ?? a.code);
      return data ?? [];
    });

const pedirArbol = () =>
  axiosInstance
    .get<{ nodes: CategoryNode[] }>(`${API_URL_PIM_PRODUCTS}/filters/categories`)
    .then(({ data }) => {
      const recorrer = (nodos: CategoryNode[]) =>
        nodos.forEach((n) => {
          nombresDeCategoria.set(n.categoryId, n.name);
          recorrer(n.children ?? []);
        });
      recorrer(data?.nodes ?? []);
      return data?.nodes ?? [];
    });

const { Text } = Typography;

type DraftFilters = Pick<
  CatalogFiltersState,
  "manufacturer" | "attributeSet" | "estado" | "atributos" | "categorias" | "dueno"
>;

const VACIO: DraftFilters = {
  manufacturer: undefined,
  attributeSet: undefined,
  estado: undefined,
  atributos: undefined,
  categorias: undefined,
  dueno: undefined,
};

/**
 * Las dos opciones del filtro de dueño, con su etiqueta: las usan el panel y los tags. En
 * pantalla se habla de participantes (xcons-docs#479); la faceta sigue siendo `hasOwner`.
 */
const DUENO: Record<"sin" | "con", string> = {
  sin: "Sin participantes",
  con: "Con participantes",
};

/**
 * Las opciones de UN atributo, con búsqueda del lado del servidor: `vv_size` tiene más de
 * 4.600 y `vv_color` más de 1.100, así que no se traen todas de una.
 */
const OpcionesDeAtributo = ({
  atributo,
  value,
  onChange,
}: {
  atributo: AtributoFiltrable;
  value: number[];
  onChange: (ids: number[]) => void;
}) => {
  const [opciones, setOpciones] = useState<OpcionDeFiltro[]>([]);
  const [cargando, setCargando] = useState(false);
  const pedido = useRef(0);

  const buscar = (search: string) => {
    const n = ++pedido.current;
    setCargando(true);
    axiosInstance
      .get<{ options: OpcionDeFiltro[] }>(
        `${API_URL_PIM_PRODUCTS}/filters/attributes/${encodeURIComponent(atributo.code)}/options`,
        { params: { search: search || undefined, limit: 50 } },
      )
      .then(({ data }) => {
        // Una respuesta vieja no pisa a la nueva: se tipea más rápido de lo que contesta.
        if (n !== pedido.current) return;
        for (const o of data.options) {
          etiquetasDeOpcion.set(claveOpcion(atributo.code, o.optionId), o.label);
        }
        setOpciones(data.options);
      })
      .catch(() => n === pedido.current && setOpciones([]))
      .finally(() => n === pedido.current && setCargando(false));
  };

  useEffect(() => {
    buscar("");
    // Sólo al montar: después busca lo que se tipea.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atributo.code]);

  // Lo ya elegido se ofrece siempre, aunque la búsqueda actual no lo traiga: si no, antd lo
  // mostraría como un número pelado.
  const opcionesConElegidas = useMemo(() => {
    const vistas = new Set(opciones.map((o) => o.optionId));
    const faltan = value
      .filter((id) => !vistas.has(id))
      .map((id) => ({
        value: id,
        label: etiquetasDeOpcion.get(claveOpcion(atributo.code, id)) ?? `#${id}`,
      }));
    return [
      ...faltan,
      ...opciones.map((o) => ({
        value: o.optionId,
        label: `${o.label} (${o.productCount})`,
      })),
    ];
  }, [opciones, value, atributo.code]);

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      filterOption={false}
      onSearch={buscar}
      notFoundContent={cargando ? <Spin size="small" /> : undefined}
      style={{ width: "100%" }}
      placeholder={`Buscar ${atributo.label ?? atributo.code}`}
      value={value}
      onChange={onChange}
      options={opcionesConElegidas}
      maxTagCount={4}
    />
  );
};

const aTreeData = (nodos: CategoryNode[]): any[] =>
  nodos.map((n) => ({
    value: n.categoryId,
    title: n.name,
    children: aTreeData(n.children ?? []),
  }));

interface CatalogFiltersFormProps {
  filters: CatalogFiltersState;
  applyFilters: (next: Partial<CatalogFiltersState>) => void;
  facetValues: (name: string) => Array<{ value: string; count: number }>;
  closeModal?: () => void;
}

const CatalogFiltersForm = ({
  filters,
  applyFilters,
  facetValues,
  closeModal,
}: CatalogFiltersFormProps) => {
  const [draft, setDraft] = useState<DraftFilters>({
    manufacturer: filters.manufacturer,
    attributeSet: filters.attributeSet,
    estado: filters.estado,
    atributos: filters.atributos,
    categorias: filters.categorias,
    dueno: filters.dueno,
  });

  // Lo que el índice puede filtrar, pedido una vez por apertura del panel.
  const [atributos, setAtributos] = useState<AtributoFiltrable[]>([]);
  const [arbol, setArbol] = useState<CategoryNode[]>([]);
  useEffect(() => {
    pedirAtributos()
      .then(setAtributos)
      .catch(() => setAtributos([]));
    pedirArbol()
      .then(setArbol)
      .catch(() => setArbol([]));
  }, []);

  const nombreDeCategoria = useMemo(() => {
    const out = new Map<number, string>();
    const recorrer = (nodos: CategoryNode[]) =>
      nodos.forEach((n) => {
        out.set(n.categoryId, n.name);
        recorrer(n.children ?? []);
      });
    recorrer(arbol);
    return out;
  }, [arbol]);

  const elegidos = Object.keys(draft.atributos ?? {});
  const atributoPorCodigo = useMemo(
    () => new Map(atributos.map((a) => [a.code, a])),
    [atributos],
  );
  const setOpcionesDe = (code: string, ids: number[]) =>
    setDraft((d) => ({ ...d, atributos: { ...(d.atributos ?? {}), [code]: ids } }));
  const sacarAtributo = (code: string) =>
    setDraft((d) => {
      const { [code]: _fuera, ...resto } = d.atributos ?? {};
      return { ...d, atributos: Object.keys(resto).length ? resto : undefined };
    });

  // Las facetas son del índice: sólo alcanzan a lo publicado. Elegir un estado sin
  // publicar en este mismo panel las esconde, igual que antes escondía el bloque entero.
  const soloSinPublicar = draft.estado === "draft" || draft.estado === "review";

  const aplicar = () => {
    // Un atributo agregado sin opciones no filtra: se descarta en vez de mandarlo vacío.
    const atributosConOpciones = Object.fromEntries(
      Object.entries(draft.atributos ?? {}).filter(([, ids]) => ids.length),
    );
    // Con Borrador o En revisión los filtros de índice no se ven ni se pueden contestar
    // (los borradores no están indexados): se limpian en vez de quedar aplicados a escondidas.
    applyFilters({
      ...draft,
      atributos:
        !soloSinPublicar && Object.keys(atributosConOpciones).length
          ? atributosConOpciones
          : undefined,
      categorias:
        !soloSinPublicar && draft.categorias?.length ? draft.categorias : undefined,
      dueno: !soloSinPublicar ? draft.dueno : undefined,
    });
    closeModal?.();
  };

  const limpiar = () => {
    setDraft(VACIO);
    applyFilters(VACIO);
    closeModal?.();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Flex vertical gap={16}>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              Estado
            </Text>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Estado"
              value={draft.estado}
              onChange={(estado) => setDraft((d) => ({ ...d, estado }))}
              // Los publicados filtran por el `state` del índice (xcons-docs#380): con
              // `status` no se podía ofrecer Archivado, que era el mismo `2` que Pausado.
              options={[
                { value: "draft", label: ESTADOS.draft.label },
                { value: "review", label: ESTADOS.review.label },
                { value: "active", label: ESTADOS.active.label },
                { value: "inactive", label: ESTADOS.inactive.label },
                { value: "archived", label: ESTADOS.archived.label },
              ]}
            />
          </div>
          {!soloSinPublicar && (
            <>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                >
                  Participantes
                </Text>
                {/* La faceta `hasOwner` del índice (xcons-docs#447), con su conteo. */}
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Participantes"
                  value={draft.dueno}
                  onChange={(dueno) => setDraft((d) => ({ ...d, dueno }))}
                  options={(["sin", "con"] as const).map((value) => {
                    const n = facetValues("hasOwner").find(
                      (f) => f.value === String(value === "con"),
                    )?.count;
                    return {
                      value,
                      label: n !== undefined ? `${DUENO[value]} (${n})` : DUENO[value],
                    };
                  })}
                />
              </div>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                >
                  Marca
                </Text>
                <Select
                  allowClear
                  // Se tipea para encontrar una, en vez de recorrer la lista entera.
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  placeholder="Marca"
                  value={draft.manufacturer}
                  onChange={(manufacturer) =>
                    setDraft((d) => ({ ...d, manufacturer }))
                  }
                  options={facetValues("manufacturer").map((f) => ({
                    value: f.value,
                    label: `${f.value} (${f.count})`,
                  }))}
                />
              </div>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                >
                  Conjunto
                </Text>
                <Select
                  allowClear
                  // Con buscador, como Marca.
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  placeholder="Conjunto"
                  value={draft.attributeSet}
                  onChange={(attributeSet) =>
                    setDraft((d) => ({ ...d, attributeSet }))
                  }
                  options={facetValues("attributeSet").map((f) => ({
                    value: f.value,
                    label: `${etiquetaConjunto(f.value)} (${f.count})`,
                  }))}
                />
              </div>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                >
                  Categoría
                </Text>
                <TreeSelect
                  multiple
                  treeCheckable
                  // Sin cascada: una categoría ya incluye sus subcategorías en el filtro, así
                  // que marcarlas a todas no cambia el resultado y llena el campo de tags.
                  treeCheckStrictly
                  allowClear
                  showSearch
                  treeNodeFilterProp="title"
                  style={{ width: "100%" }}
                  placeholder="Categoría"
                  value={(draft.categorias ?? []).map((value) => ({
                    value,
                    label: nombreDeCategoria.get(value) ?? `#${value}`,
                  }))}
                  onChange={(v) =>
                    setDraft((d) => ({
                      ...d,
                      categorias: (Array.isArray(v) ? v : [])
                        .map((x: any) => Number(x?.value ?? x))
                        .filter((n) => Number.isFinite(n)),
                    }))
                  }
                  treeData={aTreeData(arbol)}
                  maxTagCount={4}
                />
              </div>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                >
                  Atributos
                </Text>
                <Flex vertical gap={8}>
                  {elegidos.map((code) => {
                    const atributo = atributoPorCodigo.get(code);
                    if (!atributo) return null;
                    return (
                      <div key={code}>
                        <Flex justify="space-between" align="center">
                          <Text style={{ fontSize: 12 }}>{atributo.label ?? code}</Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            aria-label={`Sacar ${atributo.label ?? code}`}
                            onClick={() => sacarAtributo(code)}
                          />
                        </Flex>
                        <OpcionesDeAtributo
                          atributo={atributo}
                          value={draft.atributos?.[code] ?? []}
                          onChange={(ids) => setOpcionesDe(code, ids)}
                        />
                      </div>
                    );
                  })}
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="Agregar un atributo"
                    value={null}
                    onChange={(code: string) => setOpcionesDe(code, [])}
                    options={atributos
                      .filter((a) => !elegidos.includes(a.code))
                      .map((a) => ({ value: a.code, label: a.label ?? a.code }))}
                  />
                </Flex>
              </div>
            </>
          )}
        </Flex>
      </div>
      <Flex
        justify="end"
        gap={8}
        style={{
          flexShrink: 0,
          padding: "12px 24px",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fff",
        }}
      >
        <Button type="default" danger onClick={limpiar}>
          Borrar filtros
        </Button>
        <Button type="primary" icon={<FilterOutlined />} onClick={aplicar}>
          Filtrar
        </Button>
      </Flex>
    </div>
  );
};

interface CatalogFiltersButtonProps {
  filters: CatalogFiltersState;
  applyFilters: (next: Partial<CatalogFiltersState>) => void;
  facetValues: (name: string) => Array<{ value: string; count: number }>;
}

export const CatalogFiltersButton = ({
  filters,
  applyFilters,
  facetValues,
}: CatalogFiltersButtonProps) => {
  const activos =
    [filters.manufacturer, filters.attributeSet, filters.estado, filters.dueno].filter(Boolean)
      .length +
    Object.values(filters.atributos ?? {}).filter((ids) => ids.length).length +
    (filters.categorias?.length ? 1 : 0);

  return (
    // Mismo patrón que Dashboard/Contactos/Presupuestos: LateralModal + footer
    // "Borrar filtros" / "Filtrar" (xcons-docs#366).
    <LateralModal
      buttonName={activos ? `Filtros (${activos})` : "Filtros"}
      title="Filtros"
    >
      <CatalogFiltersForm
        filters={filters}
        applyFilters={applyFilters}
        facetValues={facetValues}
      />
    </LateralModal>
  );
};

interface CatalogFilterTagsProps {
  filters: CatalogFiltersState;
  applyFilters: (next: Partial<CatalogFiltersState>) => void;
}

/**
 * **Los filtros aplicados, a la vista** (xcons-docs#442): una etiqueta por filtro, con su
 * cruz para sacarlo, y al final la pastilla de Borrar filtros para sacarlos todos. Mismo
 * formato que `FilterTags` de Presupuestos y Contactos, que no se reusa porque lee un form
 * de refine y el Catálogo filtra con su propio estado.
 *
 * Categorías y opciones de atributo van **una etiqueta por valor**: así se saca de a una.
 * La búsqueda no lleva etiqueta: ya se ve en su campo, con su propia cruz.
 */
export const CatalogFilterTags = ({ filters, applyFilters }: CatalogFilterTagsProps) => {
  const t = useTranslate();
  // Si se llegó con filtros sin haber abierto el panel, los nombres todavía no están.
  const [, refrescar] = useState(0);
  const faltanCategorias = (filters.categorias ?? []).some(
    (id) => !nombresDeCategoria.has(id),
  );
  const faltanAtributos = Object.keys(filters.atributos ?? {}).some(
    (code) => !nombresDeAtributo.has(code),
  );
  useEffect(() => {
    if (faltanCategorias) pedirArbol().then(() => refrescar((n) => n + 1)).catch(() => {});
  }, [faltanCategorias]);
  useEffect(() => {
    if (faltanAtributos)
      pedirAtributos().then(() => refrescar((n) => n + 1)).catch(() => {});
  }, [faltanAtributos]);

  const etiquetas: Array<{ key: string; texto: React.ReactNode; sacar: () => void }> = [];

  if (filters.estado)
    etiquetas.push({
      key: "estado",
      texto: <>Estado: <span>{ESTADOS[filters.estado]?.label ?? filters.estado}</span></>,
      sacar: () => applyFilters({ estado: undefined }),
    });
  if (filters.dueno)
    etiquetas.push({
      key: "dueno",
      texto: <>Participantes: <span>{DUENO[filters.dueno]}</span></>,
      sacar: () => applyFilters({ dueno: undefined }),
    });
  if (filters.manufacturer)
    etiquetas.push({
      key: "marca",
      texto: <>Marca: <span>{filters.manufacturer}</span></>,
      sacar: () => applyFilters({ manufacturer: undefined }),
    });
  if (filters.attributeSet)
    etiquetas.push({
      key: "conjunto",
      texto: <>Conjunto: <span>{etiquetaConjunto(filters.attributeSet)}</span></>,
      sacar: () => applyFilters({ attributeSet: undefined }),
    });
  for (const id of filters.categorias ?? [])
    etiquetas.push({
      key: `categoria-${id}`,
      texto: <>Categoría: <span>{nombresDeCategoria.get(id) ?? `#${id}`}</span></>,
      sacar: () => {
        const resto = (filters.categorias ?? []).filter((c) => c !== id);
        applyFilters({ categorias: resto.length ? resto : undefined });
      },
    });
  for (const [code, ids] of Object.entries(filters.atributos ?? {}))
    for (const id of ids)
      etiquetas.push({
        key: `atributo-${code}-${id}`,
        texto: (
          <>
            {nombresDeAtributo.get(code) ?? code}:{" "}
            <span>{etiquetasDeOpcion.get(claveOpcion(code, id)) ?? `#${id}`}</span>
          </>
        ),
        sacar: () => {
          const quedan = ids.filter((x) => x !== id);
          const { [code]: _fuera, ...otros } = filters.atributos ?? {};
          const atributos = quedan.length ? { ...otros, [code]: quedan } : otros;
          applyFilters({
            atributos: Object.keys(atributos).length ? atributos : undefined,
          });
        },
      });

  if (!etiquetas.length) return null;

  return (
    <Flex className="filter-tags">
      {etiquetas.map((e) => (
        <Tag
          key={e.key}
          closable
          closeIcon={<CloseCircleOutlined />}
          onClose={(ev) => {
            // antd esconde la etiqueta por su cuenta; la saca el filtro, no el Tag.
            ev.preventDefault();
            e.sacar();
          }}
        >
          {e.texto}
        </Tag>
      ))}
      <Tag
        onClick={() => applyFilters(VACIO)}
        style={{
          fontWeight: 500,
          backgroundColor: "#fff",
          borderRadius: 37,
          cursor: "pointer",
        }}
      >
        {t("filter.button.clearFilters")}
      </Tag>
    </Flex>
  );
};
